import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { toast } from "@/lib/toast";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Activation() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const token = params.get("token") || "";

  const [state, setState] = useState("loading"); // loading | success | error
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!token) {
      setState("error");
      setMessage("Lien d'activation invalide.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/activate", { token });
        login(data.token, data.user);
        setState("success");
        setMessage(`Compte activé ! Bienvenue ${data.user.first_name}.`);
        toast.success("Compte activé", { description: "Vous êtes maintenant connecté" });
        setTimeout(() => navigate("/tableau-de-bord", { replace: true }), 1500);
      } catch (err) {
        setState("error");
        setMessage(err.response?.data?.detail || "Activation impossible.");
      }
    })();
  }, [token, login, navigate]);

  return (
    <div className="min-h-screen wallpaper flex items-center justify-center px-6" data-testid="activation-page">
      <div className="max-w-md w-full bg-card border border-vertec-borderDark rounded-xl p-10 text-center animate-fade-in-up">
        <div className="flex justify-center mb-6"><Logo size={48} withWordmark /></div>

        {state === "loading" && (
          <>
            <Loader2 className="mx-auto text-vertec-blue animate-spin mb-4" size={40} />
            <h2 className="text-2xl font-semibold mb-2">Activation en cours…</h2>
            <p className="text-muted-foreground text-sm">Merci de patienter quelques instants.</p>
          </>
        )}

        {state === "success" && (
          <>
            <CheckCircle2 className="mx-auto text-status-success mb-4" size={44} />
            <h2 className="text-2xl font-semibold mb-2">Compte activé</h2>
            <p className="text-muted-foreground text-sm">{message}</p>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle className="mx-auto text-status-danger mb-4" size={44} />
            <h2 className="text-2xl font-semibold mb-2">Activation impossible</h2>
            <p className="text-muted-foreground text-sm mb-6">{message}</p>
            <Button
              onClick={() => navigate("/connexion")}
              className="bg-vertec-blue hover:bg-vertec-blueHover text-white"
              data-testid="activation-back-to-login-btn"
            >
              Aller à la connexion
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
